/**
 * Hover explanation for a Fair Price anchor label.
 *
 * Positioned against the chart container and flipped when it would run off an
 * edge — the anchor labels are pinned to the RIGHT edge of the chart, so a
 * naive left-aligned tooltip would be clipped for every single one of them.
 */
import React from 'react';
import type { FairPriceAnchorKind } from '../../store/fairPriceStore';
import { ANCHOR_HELP, MIGRATION_CONDITIONS } from '../Chart/FairPriceAnchorHelp';
import './FairPriceAnchorTooltip.css';

interface FairPriceAnchorTooltipProps {
  kind: FairPriceAnchorKind;
  /** Cursor position in chart-container CSS pixels. */
  x: number;
  y: number;
  containerWidth: number;
  containerHeight: number;
}

const WIDTH = 260;
const MARGIN = 10;

const FairPriceAnchorTooltip: React.FC<FairPriceAnchorTooltipProps> = React.memo(
  ({ kind, x, y, containerWidth, containerHeight }) => {
    const help = ANCHOR_HELP[kind];
    if (!help) return null;

    // Prefer left of the cursor: these labels hug the right edge, so opening
    // rightwards would clip almost every time.
    let left = x - WIDTH - MARGIN;
    if (left < MARGIN) left = Math.min(x + MARGIN, containerWidth - WIDTH - MARGIN);
    if (left < MARGIN) left = MARGIN;

    // Estimate rather than measure: measuring needs a layout pass, which would
    // make the tooltip visibly jump on first paint.
    const estHeight = kind === 'migrated' ? 190 : 130;
    let top = y + MARGIN;
    if (top + estHeight > containerHeight - MARGIN) {
      top = Math.max(MARGIN, y - estHeight - MARGIN);
    }

    return (
      <div className="fp-anchor-tooltip" style={{ left, top, width: WIDTH }}>
        <div className="fp-anchor-tooltip-title">{help.title}</div>
        <div className="fp-anchor-tooltip-what">{help.what}</div>
        <div className="fp-anchor-tooltip-why">{help.why}</div>
        {kind === 'migrated' && (
          <ul className="fp-anchor-tooltip-conditions">
            {MIGRATION_CONDITIONS.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }
);

FairPriceAnchorTooltip.displayName = 'FairPriceAnchorTooltip';

export default FairPriceAnchorTooltip;
